import { useRef, useState } from "react";
import { processImage } from "@/lib/utils/image";
import { validateFile } from "@/lib/utils/file-validation";
import { usePresignedStoryUrl } from "@/lib/hooks/mentor-stories/use-mentor-stories";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { Loader2, Upload } from "lucide-react";

interface StorySlideUploadProps {
  mentorId: string;
  currentImageUrl?: string;
  onUploaded: (key: string, url: string) => void;
}

export function StorySlideUpload({
  mentorId,
  currentImageUrl,
  onUploaded,
}: StorySlideUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const presignedUrl = usePresignedStoryUrl(mentorId);
  const { url: signedImageUrl } = useSignedMedia(currentImageUrl);
  const { toast } = useToast();

  const handleFileSelect = async (file: File) => {
    const { valid, error } = validateFile(file, {
      accept: "image/png,image/jpeg,image/heic,image/heif",
      maxSize: 15 * 1024 * 1024,
    });
    if (!valid) {
      toast({ title: error, variant: "destructive" });
      return;
    }

    setIsUploading(true);
    try {
      const processed = await processImage(file);

      const { key, url: uploadUrl } = await presignedUrl.mutateAsync({
        filename: processed.name,
        contentType: processed.type,
      });

      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: processed,
        headers: { "Content-Type": processed.type },
      });

      if (!uploadResponse.ok) {
        throw new Error(`S3 upload failed: ${uploadResponse.status}`);
      }

      const displayUrl = uploadUrl.split("?")[0];
      onUploaded(key, displayUrl);
    } catch (error) {
      console.error("Upload failed:", error);
      toast({
        title: "Upload failed",
        description: "Could not upload the photo. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  return (
    <div
      className={cn(
        "cursor-pointer rounded-lg border-2 border-dashed p-4 text-center transition-colors",
        isDragging
          ? "border-primary bg-primary/5"
          : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50",
        isUploading && "pointer-events-none opacity-50",
      )}
      onClick={() => inputRef.current?.click()}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {signedImageUrl ? (
        <div className="relative">
          <img
            src={signedImageUrl}
            alt="Slide preview"
            className="mx-auto max-h-72 rounded"
          />
          <p className="text-muted-foreground mt-2 text-xs">
            Click to replace image
          </p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2 py-4">
          {isUploading ? (
            <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
          ) : (
            <Upload className="text-muted-foreground h-8 w-8" />
          )}
          <p className="text-muted-foreground text-sm">
            {isUploading ? "Uploading..." : "Click or drag to upload PNG"}
          </p>
          <p className="text-muted-foreground/60 text-xs">
            Supports JPG, PNG, HEIC
          </p>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/heic,image/heif"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFileSelect(file);
          e.target.value = "";
        }}
      />
    </div>
  );
}
